
export interface PetInfo {
    petName: string;
    petType: 'dog' | 'cat' | 'both';
}

/**
 * Extract pet name and type from conversation messages text.
 */
export const extractPetInfoFromMessages = (messages: any[]): PetInfo => {
    // 1. Extract pet type (Always scan the whole conversation for type)
    const conversationText = messages.map(msg => `${msg.role}: ${msg.content}`).join('\n');
    const hasDog = /dog|puppy|canine|perro|perrito|chien|chiot|狗|小狗|狗狗/gi.test(conversationText);
    const hasCat = /cat|kitten|feline|gato|gatito|chat|chaton|猫|小猫|猫咪/gi.test(conversationText);

    let extractedPetType: 'dog' | 'cat' | 'both' = 'both';
    if (hasDog && !hasCat) extractedPetType = 'dog';
    else if (hasCat && !hasDog) extractedPetType = 'cat';

    // 2. Extract pet name (Scan messages from NEWEST to OLDEST)
    const petNamePatterns = [
        // High confidence direct patterns (Chinese)
        /(?:我的|我们的)(?:狗|猫|狗狗|猫咪|宠物)(?:叫|名为|名字是|是)\s*([\u4e00-\u9fff\w]{1,6})/gi,
        /(?:狗|猫|狗狗|猫咪|宠物)(?:名字)?(?:叫|是)\s*([\u4e00-\u9fff\w]{1,6})/gi,
        /叫\s*([\u4e00-\u9fff\w]{1,6})(?:的)(?:狗|猫|狗狗|猫咪|宠物)/gi,

        // High priority intent patterns (English/En-like)
        /\b(?:chat|talk|ask|show|tell)\s+(?:with|about|for)\s+([A-ZÀ-ÿ][a-zÀ-ÿ]+)/gi,
        /\b(?:about|with)\s+([A-ZÀ-ÿ][a-zÀ-ÿ]+)/g,

        // Spanish: "Mi perro se llama Max", "Mi gato es Whiskers"
        /(?:mi|nuestro|el|la)\s+(?:perro|gato|mascota|cachorro|gatito)\s+(?:se\s+llama|es)\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ]+)/gi,
        /(?:perro|gato|mascota)\s+(?:llamado|llamada)\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ]+)/gi,
        /\b(?:con|sobre)\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ]+)/gi,

        // French: "Mon chat s'appelle Félix", "Mon chien est Max"
        /(?:mon|notre|le|la)\s+(?:chien|chat|animal|chiot|chaton)\s+(?:s'appelle|se\s+nomme|est)\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ]+)/gi,
        /(?:chien|chat|animal)\s+(?:nommé|appelé)\s+([A-ZÀ-ÿ][A-Za-z8-ÿ]+)/gi,
        /\b(?:avec|sur)\s+([A-ZÀ-ÿ][A-Za-zÀ-ÿ]+)/gi,

        // English patterns refined
        /(?:my|our|the|a|an)\s+(?:dog|cat|pet|puppy|kitten|狗|猫|狗狗|猫咪)\s+(?:is\s+|was\s+)?(?:named|called)\s+([A-ZÀ-ÿ\u4e00-\u9fff][wÀ-ÿ\u4e00-\u9fff]*)/gi,
        /(?:my|our|the|a|an)\s+(?:dog|cat|pet|puppy|kitten|狗|猫|狗狗|猫咪)\s+(?:is|was)\s+([A-ZÀ-ÿ\u4e00-\u9fff][wÀ-ÿ\u4e00-\u9fff]*)/gi,
        /(?:his|her|its|the|pet's)\s+name\s+is\s+([A-ZÀ-ÿ\u4e00-\u9fff][\wÀ-ÿ\u4e00-\u9fff]*)/gi,
        /(?:my|our|the)\s+(?:dog|cat|pet|puppy|kitten|狗|猫|狗狗|猫咪)\s+([A-ZÀ-ÿ\u4e00-\u9fff][\wÀ-ÿ\u4e00-\u9fff]*)/gi,
        /([A-ZÀ-ÿ\u4e00-\u9fff][\wÀ-ÿ\u4e00-\u9fff]*)(?:'s)?\s+(?:is\s+|was\s+)?(?:a|an|the|my|our)\s+(?:dog|cat|pet|puppy|kitten|狗|猫|狗狗|猫咪)/gi,

        // Chinese context patterns (More restricted)
        /[和与]\s*([\u4e00-\u9fff]{1,4})\s*聊天/,
        /关于\s*([\u4e00-\u9fff]{1,4})\s*的(?:聊天|话题|情况|记录)/,
        /([\u4e00-\u9fff]{2,4})(?:近期的)?聊天/,
        /历史(?:记录)?\s*([\u4e00-\u9fff]{1,4})/,
    ];

    // Stop words
    const stopWords = ['My', 'Our', 'The', 'Your', 'His', 'Her', 'Its', 'He', 'She', 'It', 'Is', 'Was', 'Chat', 'History', 'About', 'With', 'Recent', 'That', 'This', 'Very', 'Good', 'Bad', 'Cute', 'Please', 'Can', 'Help', '告诉我', '跟我说', '我想知道', '关于', '近期的', '最近的', '历史', '聊天'];

    // Iterate messages
    for (let i = messages.length - 1; i >= 0; i--) {
        const content = messages[i].content || '';
        if (!content) continue;

        for (const pattern of petNamePatterns) {
            pattern.lastIndex = 0;
            const match = pattern.exec(content);
            if (match && match[1]) {
                let candidate = match[1].trim();

                // Remove common prefix particles if they leaked in
                candidate = candidate.replace(/^(告诉我|跟我说|我想知道|诉我|我想|关于|最近的|近期的|的)/, '');
                candidate = candidate.replace(/(的|聊天|话题|记录|情况)$/, '');
                candidate = candidate.trim();

                if (stopWords.some(word => candidate.toLowerCase() === word.toLowerCase())) continue;

                // Basic validation
                if (candidate.length >= 1 && candidate.length <= 10) {
                    return { petName: candidate, petType: extractedPetType };
                }
            }
        }
    }

    return { petName: '', petType: extractedPetType };
};
